import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, canWrite } from "@/context/AuthContext";
import { rupiah, num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calculator, Save, TrendingUp, Leaf } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";

const COLORS = ["#15803d", "#0ea5e9", "#f59e0b", "#ef4444"];

function Row({ label, value, strong, accent }) {
  return (
    <div className={`flex items-center justify-between py-2.5 px-1 ${strong ? "border-t border-slate-200" : ""}`}>
      <span className={`text-sm ${strong ? "font-semibold text-slate-900" : "text-slate-600"}`}>{label}</span>
      <span className={`tabular-nums ${strong ? "font-bold text-base " + (accent || "text-slate-900") : "text-slate-700"}`}>{rupiah(value)}</span>
    </div>
  );
}

export default function HppCalculation() {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [margin, setMargin] = useState(20);
  const [data, setData] = useState(null);
  const writable = canWrite(user?.role, ["akuntansi"]);

  useEffect(() => {
    api.get("/batches").then((r) => {
      setBatches(r.data);
      if (r.data[0]) setBatchId(r.data[0].id);
    });
  }, []);

  const calc = useCallback(() => {
    if (!batchId) return;
    api.get(`/hpp/${batchId}`, { params: { margin_pct: margin } }).then((r) => setData(r.data));
  }, [batchId, margin]);

  useEffect(() => { calc(); }, [calc]);

  const save = async () => {
    try {
      await api.post("/hpp/save", { batch_id: batchId, margin_pct: Number(margin) });
      toast.success("Hasil HPP tersimpan");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-slate-900">HPP Calculation</h2>
        <p className="text-sm text-slate-500 mt-1">Perhitungan Green HPP transparan & akuntabel per batch produksi.</p>
      </div>

      <Card className="p-5 border-slate-200 shadow-sm">
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-semibold text-slate-600">Pilih Batch Produksi</Label>
            <Select value={batchId} onValueChange={setBatchId}>
              <SelectTrigger data-testid="hpp-batch-select"><SelectValue placeholder="Pilih batch" /></SelectTrigger>
              <SelectContent>
                {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.no_batch} — {b.product_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Margin (%)</Label>
            <Input type="number" value={margin} onChange={(e) => setMargin(e.target.value)} data-testid="hpp-margin-input" />
          </div>
        </div>
      </Card>

      {data && (
        <>
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Cost breakdown */}
            <Card className="lg:col-span-2 p-6 border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Calculator className="h-5 w-5 text-green-700" />
                <h3 className="font-heading font-semibold text-slate-900">Komponen Perhitungan — {data.no_batch} ({data.product_name})</h3>
              </div>
              <div className="space-y-0.5">
                <Row label="Biaya Bahan Baku (BBB)" value={data.bbb} />
                <Row label="Biaya Tenaga Kerja Langsung (BTKL)" value={data.btkl} />
                <Row label="Biaya Overhead Pabrik (BOP)" value={data.bop} />
                <Row label="HPP Tradisional" value={data.hpp_tradisional} strong />
                <Row label="Biaya Lingkungan" value={data.env_total} />
                <Row label="Green HPP" value={data.green_hpp} strong accent="text-green-700" />
                <Row label={`HPP per ${data.satuan} — Produk Baik (${num(data.good_volume, 1)} ${data.satuan} dari volume ${num(data.volume, 1)})`} value={data.hpp_per_unit} strong accent="text-blue-700" />
                {data.hpp_per_unit_baik != null && (
                  <Row label={`HPP per Unit Produk Baik (${num(data.unit_baik)} unit${data.unit_cacat ? `, cacat ${num(data.unit_cacat)}` : ""})`} value={data.hpp_per_unit_baik} strong accent="text-blue-700" />
                )}
                <Row label={`Harga Jual Rekomendasi (margin ${data.margin_pct}%)`} value={data.selling_price} strong accent="text-green-700" />
              </div>
              {writable && (
                <Button onClick={save} className="mt-4 bg-green-700 hover:bg-green-800" data-testid="hpp-save-btn">
                  <Save className="h-4 w-4 mr-1.5" /> Simpan Hasil HPP
                </Button>
              )}
            </Card>

            {/* Cost composition pie */}
            <Card className="p-5 border-slate-200 shadow-sm">
              <h3 className="font-heading font-semibold text-slate-900 mb-3">Komposisi Biaya</h3>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                    data={[
                      { name: "BBB", value: data.bbb }, { name: "BTKL", value: data.btkl },
                      { name: "BOP", value: data.bop }, { name: "Lingkungan", value: data.env_total },
                    ]}>
                    {COLORS.map((c) => <Cell key={c} fill={c} />)}
                  </Pie>
                  <Tooltip formatter={(v) => rupiah(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Comparison */}
          <Card className="p-6 border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-green-700" />
              <h3 className="font-heading font-semibold text-slate-900">Perbandingan: Konvensional vs Ramah Lingkungan</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 items-center">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={[
                  { name: "HPP Tradisional", value: data.hpp_trad_per_unit },
                  { name: "Green HPP", value: data.hpp_per_unit },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}jt`} />
                  <Tooltip formatter={(v) => rupiah(v)} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    <Cell fill="#94a3b8" /><Cell fill="#15803d" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-600">HPP Tradisional per {data.satuan}</span>
                  <span className="font-semibold tabular-nums">{rupiah(data.hpp_trad_per_unit)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50">
                  <span className="text-sm text-green-800 flex items-center gap-1.5"><Leaf className="h-4 w-4" /> Green HPP per {data.satuan}</span>
                  <span className="font-semibold tabular-nums text-green-700">{rupiah(data.hpp_per_unit)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50">
                  <span className="text-sm text-amber-800">Selisih (Internalisasi Biaya Lingkungan)</span>
                  <span className="font-semibold tabular-nums text-amber-700">{rupiah(data.comparison.selisih)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-red-50">
                  <span className="text-sm text-red-800">Total Emisi CO₂</span>
                  <span className="font-semibold tabular-nums text-red-700">{num(data.total_emisi_kg, 1)} kg</span>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
